import * as Config from 'show-page/constants/config';
import * as LaunchDateOrder from 'show-page/constants/launch-date-order';
import * as Mobile from 'common/constants/mobile';
import * as SeasonOrder from 'show-page/constants/season-order';
import * as ShowType from 'show-page/constants/show-type';
import * as VideoType from 'common/constants/video-type';
import * as VideoUtils from 'common/utils/video-utils';
import CVPAuthStore from 'common/stores/cvp-auth';
import Environment from 'common/utils/environment';
import Season from 'show-page/models/season';
import Video from 'show-page/models/video';
import VideoSearch from 'show-page/models/video-search';
import { action, asMap, autorun, computed, extendObservable, observable, reaction } from 'mobx';
import { browserHistory } from 'react-router';
import { find, findIndex, get, max, maxBy, omit, sortBy } from 'lodash';


class ShowStore {
    @observable currentRoute = {
        show: null,
        video: null
    };
    @observable currentSearchQuery = '';
    @observable launchDateOrder = LaunchDateOrder.ASC;
    @observable onlineExclusives = [];
    @observable searches = asMap({});
    @observable seasonOrder = SeasonOrder.CHRONOLOGICAL;
    @observable seasons = [];
    @observable selectedSeasonFilter;
    @observable sluggedVideo = null;
    @observable trailer = null;
    @observable videos = [];


    constructor(data) {
        const props = omit(data, ['onlineExclusives', 'sluggedVideo', 'trailer', 'videos']);
        const { onlineExclusives, sluggedVideo, trailer, videos } = data;
        this.sluggedVideo = sluggedVideo ? new Video(this, sluggedVideo) : null;
        this.setOnlineExclusives(onlineExclusives);
        this.setTrailer(trailer);
        this.addVideos(videos);
        extendObservable(this, props);
        this.pageTitleListener = autorun(() => this.updatePageTitle());
        this.seasonNumbersListener = reaction(
            () => this.seasonNumbers,
            (seasonNumbers) => seasonNumbers.forEach(number => this.addSeason(number)),
            true
        );
        this.currentSeasonNumberListener = reaction(
            () => parseInt(this.currentSeasonNumber, 10),
            (seasonNumber) => this.setSeasonFilter(seasonNumber)
        );
    }


    destroy() {
        this.pageTitleListener();
        this.pageTitleListener = null;
        this.seasonNumbersListener();
        this.seasonNumbersListener = null;
        this.currentSeasonNumberListener();
        this.currentSeasonNumberListener = null;
        this.onlineExclusives.forEach(video => video.destroy());
        this.onlineExclusives = [];
        this.seasons = [];
        this.videos.forEach(video => video.destroy());
        this.videos = [];
        this.sluggedVideo && this.sluggedVideo.destroy();
    }


    gotoLanding() {
        browserHistory.push(this.baseUrl);
    }


    openAppLink() {
        setTimeout(() => {
            if (Environment.andriod) {
                window.location = Mobile.ANDROID_STORE_LINK;
            } else if (Environment.ios) {
                window.location = Mobile.IOS_STORE_LINK;
            }
        }, 25);
        window.location = this.appLink;
    }


    playNextVideo() {
        browserHistory.push(this.nextRouteUrl);
    }


    updatePageTitle() {
        if (this.selectedVideo) {
            document.title = `${this.selectedVideo.title} - ${this.title} - Adult Swim Shows`;
        } else {
            document.title = this.metadata.title;
        }
    }


    /*=======================================
    =            Computed Values            =
    =======================================*/

    @computed get appLink() {
        const appLink = `${Mobile.APP_BASE_DEEP_LINK}?section=video&campaign=test&collection=${this.collection.id}`;
        return this.selectedVideo ? (appLink + '&title=' + this.selectedVideo.id) : appLink;
    }


    @computed get baseUrl() {
        return `${Config.SHOWS_BASE_URL}/${this.currentRoute.show}`;
    }


    @computed get clips() {
        const clips = this.videos.filter(video => video.type === VideoType.CLIP);
        return VideoUtils.sortVideos(clips, this);
    }


    @computed get clipsSeasonNumbers() {
        const seasonNumbers = this.seasons
            .filter(season => season.clips.length)
            .map(season => season.number);
        return sortBy(seasonNumbers, x => x || Number.POSITIVE_INFINITY);
    }


    @computed get currentSeasonNumber() {
        return get(this, 'selectedVideo.seasonNumber') || get(this, 'selectedSeason.number', null);
    }


    @computed get episodes() {
        const episodes = this.videos.filter(video => video.type === VideoType.EPISODE);
        return VideoUtils.sortVideos(episodes, this);
    }


    @computed get hasAuthEpisodes() {
        return !!find(this.episodes, { auth: true });
    }


    @computed get latestSeason() {
        return maxBy(this.seasons, season => season.number);
    }


    @computed get nextRouteUrl() {
        return (this.nextVideo) ? this.nextVideo.url : this.baseUrl;
    }


    @computed get nextVideo() {
        const id = this.selectedVideoId;
        const currentIndex = findIndex(this.playlist, { id });
        const nextIndex = (currentIndex + 1 >= this.playlist.length) ? 0 : currentIndex + 1;
        return this.playlist[nextIndex];
    }


    @computed get playlist() {
        const type = get(this, 'selectedVideo.type');
        const isOnlineOriginal = get(this, 'selectedVideo.isOnlineOriginal');
        let playlist;
        if (type === VideoType.EPISODE || this.selectedVideo === this.sluggedVideo) {
            playlist = this.episodes;
        } else if (type === VideoType.CLIP && !isOnlineOriginal) {
            playlist = this.clips;
        } else if (isOnlineOriginal) {
            playlist = this.onlineExclusives;
        } else {
            playlist = this.sortedVideos;
        }
        return playlist.filter(v => {
            return !v.expired && ((!CVPAuthStore.isAuthenticated || Environment.mobile) ? !v.auth : true);
        });
    }


    @computed get searchResults() {
        return this.currentSearchQuery ? this.searches.get(this.currentSearchQuery) : null;
    }


    @computed get selectedSeason() {
        const number = (this.selectedSeasonFilter > -1) ? this.selectedSeasonFilter : max(this.clipsSeasonNumbers);
        return find(this.seasons, { number });
    }


    @computed get selectedVideo() {
        const slug = this.currentRoute.video;
        const video = slug ? find(this.sortedVideos, { slug }) : null;
        return video || null;
    }


    @computed get selectedVideoId() {
        return this.selectedVideo ? this.selectedVideo.id : null;
    }


    @computed get seasonNames() {
        return VideoUtils.getSeasonNames(this.collection.seasons, this.videos);
    }


    @computed get seasonNumbers() {
        return VideoUtils.getSeasonNumbers(this.videos);
    }


    @computed get shareUrl() {
        return Config.BASE_URL + this.baseUrl;
    }


    @computed get showClipsNav() {
        return (this.clipsEnabled && this.collection.type === ShowType.TV_SERIES && this.clips.length > 0);
    }


    @computed get sortedSeasons() {
        const chronological = sortBy(this.seasons, ({ number }) => parseInt(number, 10) || Number.POSITIVE_INFINITY);
        switch (this.seasonOrder) {
            case SeasonOrder.ALPHABETICAL:
                return sortBy(this.seasons, 'name');
            case SeasonOrder.LATEST_FIRST:
                return [...chronological.splice(chronological.indexOf(this.latestSeason), 1), ...chronological];
            case SeasonOrder.REVERSE_CHRONOLOGICAL:
                return sortBy(this.seasons, ({ number }) => -(parseInt(number, 10) || Number.NEGATIVE_INFINITY));
            case SeasonOrder.CHRONOLOGICAL:
            default:
                return chronological;
        }
    }


    @computed get sortedSeasonNumbers() {
        return this.sortedSeasons.map(season => season.number);
    }


    @computed get sortedVideos() {
        const currentSearch = this.searchResults ? this.searchResults.videos : [];
        const sluggedVideo = this.sluggedVideo ? [ this.sluggedVideo ] : []
        return [ ...currentSearch, ...this.episodes, ...this.clips, ...this.onlineExclusives, ...sluggedVideo ];
    }


    @computed get type() {
        return get(this, 'collection.type');
    }


    /*===============================
    =            Actions            =
    ===============================*/

    @action addSeason = (number) => {
        if (find(this.seasons, { number })) return;
        this.seasons.push(new Season(this, number));
    };


    @action addVideo = (data) => {
        if (!data) return null
        const existing = find(this.videos, { id: data.id });
        if (existing) return existing;
        const video = new Video(this, data);
        this.videos.push(video);
        return video;

    };


    @action addVideos = (videos) => {
        videos = videos || [];
        return videos.filter(Boolean).map(this.addVideo);
    };


    @action clearSearchQuery = () => {
        this.currentSearchQuery = '';
    };


    @action searchClips = (query) => {
        const Q = query.trim();
        if (!Q) return;
        if (!this.searches.has(Q)) {
            const videoSearch = new VideoSearch(this, Q);
            this.searches.set(Q, videoSearch);
            videoSearch.search();
        }
        this.currentSearchQuery = Q;
        return this.searches.get(Q);
    };


    @action setCurrentRoute = ({ params }) => {
        this.currentRoute.show = params.show || null;
        this.currentRoute.video = params.video || null;
    };


    @action setOnlineExclusives = (onlineExclusives) => {
        this.onlineExclusives = (onlineExclusives || []).map(clip => new Video(this, clip));
    };


    @action setSeasonFilter = (seasonNumber) => {
        if (this.seasonNumbers.indexOf(seasonNumber) !== -1) {
            this.selectedSeasonFilter = seasonNumber;
        }
    };


    @action setTrailer = (trailer) => {
        this.trailer = trailer ? new Video(this, trailer) : null;
    };


    /*======================================
    =            Event Handlers            =
    ======================================*/

    handleRouteChange = (prevState, nextState) => {
        this.setCurrentRoute(nextState);
    };


    handleRouteEnter = (nextState) => {
        this.setCurrentRoute(nextState);
    };

}


export default ShowStore;



// WEBPACK FOOTER //
// ./src/show-page/stores/show.js